import { yupResolver } from "@hookform/resolvers/yup";
import { DialogContent } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import ReactHookFields from "src/components/shared/ReactHookForm";
import useCustomerSchema from "./utils/useCustomerSchema";
import useEmployeeCreate from "src/hooks/user/useEmployeeCreate";
import { enqueueSnackbar } from "notistack";
import useAppGlobal from "src/context/useAppGlobal";
import useEmployeeDetail from "src/hooks/user/useEmployeeDetail";
import useEmployeeUpdate from "src/hooks/user/useEmployeeUpdate";
import { userRoleType, userType } from "src/constant";
import useAuthGlobal from "src/context/useAuthGlobal";
import useUserReset from "src/hooks/user/useUserReset";

export default function UserCreate({ item }) {
  const { closeDialog } = useAppGlobal();
  const { userMe } = useAuthGlobal();

  const { trigger: createCustomer } = useEmployeeCreate();
  const { trigger: updateCustomer } = useEmployeeUpdate(item?._id);
  const { userFieldList, validationSchema, userObject } = useCustomerSchema({ item });
  const { data: userDetail, isLoading } = useEmployeeDetail(item?._id);
  const { trigger: updatePassword } = useUserReset(item?._id);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
    defaultValues: userObject,
    shouldUnregister: false,
  });

  const userTypePick = watch("type");

  useEffect(() => {
    if (!isLoading) {
      if (!!userDetail?.data) {
        reset({
          warehouse:
            {
              ...userDetail?.data?.warehouse,
              id: userDetail?.data?.warehouse?._id,
              labelSelect: userDetail?.data?.warehouse?.name,
            } ?? undefined,
          role:
            {
              ...userDetail?.data?.role,
              id: userDetail?.data?.role?._id,
              labelSelect: userDetail?.data?.role?.name,
            } ?? undefined,
          type: userDetail?.data?.type ?? undefined,
          username: userDetail?.data?.username ?? undefined,
          password: userDetail?.data?.password ?? undefined,
          fullName: userDetail?.data?.fullName ?? undefined,
          phone: userDetail?.data?.phone ?? undefined,
          email: userDetail?.data?.email ?? undefined,
        });
      } else {
        if (userMe?.role?.name !== userRoleType.superAdmin) {
          reset({
            warehouse:
              {
                ...userMe?.warehouse,
                id: userMe?.currentWarehouse?._id,
                labelSelect: userMe?.warehouse?.name,
              } ?? undefined,
          });
        }
      }
    }
  }, [userDetail?.data, isLoading, userMe?.role?.name]);

  const handleUpdatePassword = async (data) => {
    const res = await updatePassword({
      body: {
        password: data?.password,
      },
    });
    if (!res?.success) {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const handleUserCreate = async (data) => {
    let res;
    if (!item) {
      res = await createCustomer({
        body: { ...data, warehouse: data?.warehouse?._id, role: data?.role?._id },
      });
    } else {
      res = await updateCustomer({
        body: { ...data, warehouse: data?.warehouse?._id, role: data?.role?._id },
      });
    }
    if (res?.success) {
      if (!!item) {
        await handleUpdatePassword(data);
      }
      closeDialog();
      reset();
      return enqueueSnackbar("Thành công!", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const resolveRequired = (field) => {
    if (["warehouse", "role"].includes(field?.name)) {
      return userTypePick !== userType.sales;
    }
    if (["password", "repassword"].includes(field?.name)) {
      return !item;
    }
    return !!field?.required;
  };

  return (
    <DialogContent>
      <ReactHookFields
        isLoading={isLoading}
        onSubmit={handleSubmit(handleUserCreate)}
        listFields={userFieldList.map((v) => ({
          ...v,
          required: resolveRequired(v),
          display: ["warehouse", "role"]?.includes(v?.name) && userTypePick === userType.sales ? false : true,
        }))}
        control={control}
        errors={errors}
        btnFullWidth={true}
      />
    </DialogContent>
  );
}
